/*
 * Copyright (c) 2023 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { HttpClient, HttpErrorResponse, HttpHeaders, HttpStatusCode } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, mergeMap } from 'rxjs/operators';
import { ConfigService } from './config.service';

@Injectable({
  providedIn: 'root'
})
export class VehicleAttributesService {

  constructor(private readonly configService: ConfigService,
              private readonly http: HttpClient) {
  }

  private baseURL = this.configService.baseApiUrl + 'manage-attributes/vehicle-attribute';

  /**
   * Return a list of any non compliant program code.
   */
  getNonCompliantProgramCodes(): Observable<any>  {
    const url = this.baseURL + '/non-compliant-program-codes';
    return this.http.get<any>(url).pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Return data for selected attribute.
   * @param User's selection from dropdown menu.
   */
  getFilterOptions(selectedAttribute, selectedRegion, selectedSubRegion, selectedPlanningMarket, selectedArchitecture,
                   selectedMarketDivision, selectedBrand, selectedLocalBrand, selectedProgCode,
                   selectedStyleCode, selectedBodyStyle, selectedVehicleType): Observable<any> {
    const url = this.baseURL + '/get-filter-option';
    return this.http.post<any>(url,
      {
        selectedAttribute, selectedRegion, selectedSubRegion, selectedPlanningMarket, selectedArchitecture,
        selectedMarketDivision, selectedBrand, selectedLocalBrand, selectedProgCode,
        selectedStyleCode, selectedBodyStyle, selectedVehicleType
      })
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Return data that will be updated if a change in value has been committed.
   * @param User's selection from dropdown menu.
   */
  getPreviewChanges(selectedAttribute, currentValue, newValue, selectedRegion, selectedSubRegion,
                    selectedPlanningMarket, selectedArchitecture,
                    selectedMarketDivision, selectedBrand, selectedLocalBrand, selectedProgCode,
                    selectedStyleCode, selectedBodyStyle, selectedVehicleType): Observable<any> {
    const headers = new HttpHeaders();
    headers['Content-Disposition'] = 'attachment; filename=ChangeAttribute.xlsx';
    const url = this.baseURL + '/preview-changes';
    return this.http.post(url, {
      selectedAttribute, currentValue, newValue,
      selectedRegion, selectedSubRegion,
      selectedPlanningMarket, selectedArchitecture,
      selectedMarketDivision, selectedBrand, selectedLocalBrand, selectedProgCode,
      selectedStyleCode, selectedBodyStyle, selectedVehicleType
    }, {headers, responseType: 'blob'})
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Return data with old and new values.
   * @param User's selection from dropdown menu.
   */
  getCommitChanges(selectedAttribute, currentValue, newValue, selectedRegion, selectedSubRegion,
                   selectedPlanningMarket, selectedArchitecture,
                   selectedMarketDivision, selectedBrand, selectedLocalBrand, selectedProgCode,
                   selectedStyleCode, selectedBodyStyle, selectedVehicleType, userGmid): Observable<any> {
    const headers = new HttpHeaders();
    headers['Content-Disposition'] = 'attachment; filename=ChangeAttribute.xlsx';
    const url = this.baseURL + '/commit-changes';
    return this.http.post(url, {
      selectedAttribute, currentValue, newValue,
      selectedRegion, selectedSubRegion,
      selectedPlanningMarket, selectedArchitecture,
      selectedMarketDivision, selectedBrand, selectedLocalBrand, selectedProgCode,
      selectedStyleCode, selectedBodyStyle, selectedVehicleType, userGmid
    }, {headers, responseType: 'blob'})
    .pipe(catchError(myError => this.handleErrorBlob(myError)));
  }

  /**
   * Blob requests return their error body as a Blob, so it has to be read before the message can be shown.
   * @param errorResponse the error returned by a blob request
   */
  handleErrorBlob(errorResponse: HttpErrorResponse): Observable<any> {
    if (!(errorResponse.error instanceof Blob)) {
      return this.handleError(errorResponse);
    }
    const errorBlob = errorResponse.error;
    return new Observable<string>(observer => {
      const reader = new FileReader();
      reader.onloadend = () => {
        observer.next(reader.result as string);
        observer.complete();
      };
      reader.readAsText(errorBlob);
    }).pipe(
      mergeMap(text => {
        let body = null;
        try {
          body = JSON.parse(text);
        } catch {
          body = null;
        }
        return this.handleError(new HttpErrorResponse({
          error: body,
          status: errorResponse.status,
          statusText: errorResponse.statusText,
          url: errorResponse.url ?? undefined
        }));
      })
    );
  }

  handleError(errorResponse: HttpErrorResponse): any {
    let errorMessage = 'An unexpected error occurred';
    switch (errorResponse.status) {
      case HttpStatusCode.BadRequest: {
        if (errorResponse.error && errorResponse.error.message) {
          errorMessage = errorResponse.error.message;
        } else {
          errorMessage = 'Error getting data.';
        }
        break;
      }
    }
    return throwError(errorMessage);
  }

}
